// src/components/grid/ReadOnlyGridViewer.tsx

import React, { useMemo } from 'react';
import { EyeOff, User } from 'lucide-react';
import { DEFAULT_MINUTES_PER_LINE } from '../../types/schoolConfig';
import { displayLearningArea } from '../../constants/learningAreas';
import { classMatchesLeaderArea } from '../../utils/curriculumAreaScope';

interface ReadOnlyGridViewerProps {
  grid: any;
  curriculumAreaFilter?: string; // Optional filter for curriculum leaders
  semesterFilter?: 'all' | 'semester1' | 'semester2' | 'fullyear';
  teacherFilter?: string;
  classFilter?: string;
  staff?: any[]; // Staff data for looking up names
}

const ReadOnlyGridViewer: React.FC<ReadOnlyGridViewerProps> = ({
  grid,
  curriculumAreaFilter,
  semesterFilter = 'all',
  teacherFilter,
  classFilter,
  staff = []
}) => {
  // Get unique classes grouped by className (subject)
  const subjects = useMemo(() => {
    if (!grid || !grid.classes || grid.classes.length === 0) return [];

    const classData = grid.classes
      .filter((c: any) => {
        // Apply the curriculum leader’s learning-area scope if provided. Matching
        // is by canonical learning area, not raw string: a leader recorded as
        // "Health & PE" owns classes filed on the grid as "HPE" (issue #187).
        if (curriculumAreaFilter && !classMatchesLeaderArea(c, curriculumAreaFilter, grid?.name)) {
          return false;
        }

        // Apply class name filter if provided
        if (classFilter && classFilter.trim()) {
          const className = (c.className || c.subjectName || '').toLowerCase();
          const filterLower = (classFilter || '').toLowerCase();
          if (!className.includes(filterLower)) return false;
        }

        // Apply teacher filter if provided
        if (teacherFilter) {
          const lineAssignments = c.lineAssignments || {};
          const hasTeacher = Object.values(lineAssignments).some((assignment: any) => {
            // Handle different assignment formats
            if (Array.isArray(assignment)) {
              return assignment.some((a: any) => a.teacherCode === teacherFilter);
            }
            if (typeof assignment === 'string') {
              // Try parsing JSON string first
              if (assignment.trim().startsWith('[') || assignment.trim().startsWith('{')) {
                try {
                  const parsed = JSON.parse(assignment);
                  if (Array.isArray(parsed)) {
                    return parsed.some((a: any) => a.teacherCode === teacherFilter);
                  } else if (parsed.teacherCode) {
                    return parsed.teacherCode === teacherFilter;
                  }
                } catch (e) {
                  // Not valid JSON, treat as plain teacher code
                  return assignment === teacherFilter;
                }
              }
              return assignment === teacherFilter;
            }
            if (typeof assignment === 'object' && assignment.teacherCode) {
              return assignment.teacherCode === teacherFilter;
            }
            return false;
          });
          if (!hasTeacher) return false;
        }

        return true;
      })
      .map((c: any) => ({
        code: c.classCode || c.subjectCode || c.id,
        name: c.className || c.subjectName || 'Unknown',
        curriculumArea: c.curriculumArea || 'Not Set',
        yearLevel: c.yearLevel || '-',
        lineAssignments: c.lineAssignments || {},
        studyPeriod: c.studyPeriod || 'unknown'
      }));

    // Remove duplicates by unique code (not by name) and sort
    // This ensures all individual classes show up, even if they share the same name
    const uniqueSubjects = Array.from(
      new Map(classData.map((s: any) => [s.code, s])).values()
    ).sort((a: any, b: any) => a.name.localeCompare(b.name));

    return uniqueSubjects;
  }, [grid, curriculumAreaFilter, classFilter, teacherFilter]);

  // Get lines/periods from grid with semester filtering
  const lines = useMemo(() => {
    if (!grid || !grid.lines || grid.lines.length === 0) return [];

    let filteredLines = grid.lines.map((line: any) => ({
      id: line.id || line,
      name: line.name || `Line ${line.id || line}`,
      studyPeriod: line.studyPeriod || line.semester || 'unknown',
      days: line.days || [],
      minutesPerFortnight: line.minutesPerFortnight || grid.defaultClassMinutes || DEFAULT_MINUTES_PER_LINE
    }));

    // Apply semester filter
    if (semesterFilter && semesterFilter !== 'all') {
      filteredLines = filteredLines.filter((line: any) => {
        const linePeriod = (line.studyPeriod || '').toLowerCase();
        return linePeriod === (semesterFilter || '').toLowerCase();
      });
    }

    return filteredLines;
  }, [grid, semesterFilter]);

  // Get assigned staff for a specific class and line
  // Returns normalized array of teacher assignments
  const getAssignedStaff = (subjectCode: string, lineId: string) => {
    const classItem = grid.classes.find((c: any) =>
      (c.classCode || c.subjectCode || c.id) === subjectCode &&
      c.lineAssignments &&
      c.lineAssignments[lineId]
    );

    if (classItem && classItem.lineAssignments && classItem.lineAssignments[lineId]) {
      let assignment = classItem.lineAssignments[lineId];

      // Handle different formats:
      // 1. Array of objects: [{"teacherCode":"BST","minutes":30}, ...]
      if (Array.isArray(assignment)) {
        return assignment;
      }

      // 2. JSON string that needs parsing: "[{\"teacherCode\":\"BST\"...}]"
      if (typeof assignment === 'string') {
        // Try to parse as JSON first
        if (assignment.trim().startsWith('[') || assignment.trim().startsWith('{')) {
          try {
            const parsed = JSON.parse(assignment);
            // After parsing, handle the result recursively
            if (Array.isArray(parsed)) {
              return parsed;
            } else if (typeof parsed === 'object' && parsed.teacherCode) {
              return [parsed];
            }
          } catch (e) {
            // Not valid JSON, treat as plain teacher code
            return [{ teacherCode: assignment, minutes: null }];
          }
        } else {
          // Plain teacher code string like "BST"
          return [{ teacherCode: assignment, minutes: null }];
        }
      }

      // 3. Object: {"teacherCode":"BST","minutes":30}
      if (typeof assignment === 'object' && assignment.teacherCode) {
        return [assignment];
      }
    }

    return null;
  };

  // Get staff name by code
  const getStaffName = (staffCode: string) => {
    const staffMember = staff.find((s: any) => s.initial === staffCode || s.code === staffCode);
    return staffMember?.name || staffCode;
  };

  // Format study period for display
  const formatStudyPeriod = (studyPeriod: string) => {
    switch (studyPeriod) {
      case 'semester1':
        return 'S1';
      case 'semester2':
        return 'S2';
      case 'fullyear':
        return 'FY';
      default:
        return studyPeriod?.toUpperCase() || '-';
    }
  };

  // Format days for display
  const formatDays = (days: string[]) => {
    if (!days || days.length === 0) return '-';
    return days.map(day => day.charAt(0).toUpperCase() + day.slice(1, 3)).join(', ');
  };

  if (!grid) {
    return (
      <div className="text-center py-12 text-ink-500">
        <p>No grid data available</p>
      </div>
    );
  }

  if (subjects.length === 0) {
    return (
      <div className="text-center py-12 text-ink-500">
        <EyeOff className="h-12 w-12 mx-auto mb-3 text-ink-400" />
        <p>No classes found for this learning area</p>
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden">
      {/* Info Banner */}
      <div className="mb-4 bg-navy-50 border border-navy-200 rounded-md p-3 flex items-center">
        <EyeOff className="h-5 w-5 text-navy-600 mr-2 flex-shrink-0" />
        <div className="text-sm text-navy-800">
          <strong>Read-Only View</strong> - This is a view-only display of the grid. You cannot make changes here.
        </div>
      </div>

      {/* Grid Statistics */}
      <div className="mb-4 grid grid-cols-3 gap-4">
        <div className="bg-ink-50 rounded-md p-3 border border-ink-200">
          <div className="text-xs text-ink-500 uppercase tracking-wide">Classes</div>
          <div className="font-mono tabular-nums text-2xl font-bold text-ink-900">{subjects.length}</div>
        </div>
        <div className="bg-ink-50 rounded-md p-3 border border-ink-200">
          <div className="text-xs text-ink-500 uppercase tracking-wide">Lines</div>
          <div className="font-mono tabular-nums text-2xl font-bold text-ink-900">{lines.length}</div>
        </div>
        <div className="bg-ink-50 rounded-md p-3 border border-ink-200">
          <div className="text-xs text-ink-500 uppercase tracking-wide">Minutes/Line</div>
          <div className="font-mono tabular-nums text-2xl font-bold text-ink-900">{grid.defaultClassMinutes || DEFAULT_MINUTES_PER_LINE}</div>
        </div>
      </div>

      {/* Grid Table */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="w-full divide-y divide-ink-200" style={{ minWidth: 'max-content' }}>
          <thead>
            <tr className="bg-ink-50">
              <th className="px-4 py-3 text-left text-xs font-medium text-ink-700 uppercase tracking-wider sticky left-0 bg-ink-100 z-20 border-r border-ink-300 min-w-[280px]">
                <div>Class / Subject</div>
                <div className="text-xs font-normal normal-case text-ink-500 mt-1">
                  Learning Area • Year Level
                </div>
              </th>
              {lines.map((line: any) => (
                <th
                  key={line.id}
                  className="px-3 py-3 text-center text-xs font-medium text-ink-700 uppercase tracking-wider border-r border-ink-300 min-w-[140px]"
                >
                  <div className="font-mono tabular-nums font-semibold">{line.name}</div>
                  <div className="font-mono tabular-nums text-xs font-normal normal-case text-ink-500 mt-1">
                    {formatStudyPeriod(line.studyPeriod)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-ink-200">
            {subjects.map((subject: any, idx: number) => (
              <tr key={idx} className="hover:bg-ink-50 transition-colors">
                <td className="px-4 py-3 text-sm sticky left-0 bg-white z-10 border-r border-ink-200 shadow-sm min-w-[280px]">
                  <div className="font-medium text-ink-900">{subject.name}</div>
                  <div className="text-xs text-ink-500 mt-1">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-navy-100 text-navy-800">
                      {displayLearningArea(subject.curriculumArea)}
                    </span>
                    <span className="ml-2 text-ink-400">Year {subject.yearLevel}</span>
                  </div>
                </td>
                {lines.map((line: any) => {
                  const assignments = getAssignedStaff(subject.code, line.id);
                  const isAssigned = !!assignments && assignments.length > 0;

                  return (
                    <td
                      key={line.id}
                      className={`px-3 py-3 text-sm text-center border-r border-ink-200 min-w-[140px] ${
                        isAssigned ? 'bg-ok-bg' : 'bg-ink-50'
                      }`}
                    >
                      {isAssigned ? (
                        <div className="flex flex-col items-center space-y-1">
                          {assignments!.map((assignment: any, idx: number) => (
                            <div key={idx} className="flex flex-col items-center">
                              <div className="flex items-center justify-center">
                                <User className="h-3 w-3 text-ok-fg mr-1" />
                                <span className="font-mono tabular-nums font-medium text-ok-fg">{assignment.teacherCode}</span>
                              </div>
                              <span className="text-xs text-ok-fg">
                                {getStaffName(assignment.teacherCode)}
                              </span>
                              {assignment.minutes && (
                                <span className="font-mono tabular-nums text-xs text-ink-500">
                                  {assignment.minutes} min
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-xs text-ink-400 italic">
                          Unassigned
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center justify-end space-x-4 text-xs text-ink-500">
        <div className="flex items-center">
          <div className="w-4 h-4 bg-ok-bg border border-ok-border rounded mr-2"></div>
          <span>Assigned</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 bg-ink-50 border border-ink-200 rounded mr-2"></div>
          <span>Unassigned</span>
        </div>
      </div>
    </div>
  );
};

export default ReadOnlyGridViewer;
